import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { createTargetInputSchema, currencySchema, unitSchema, type CreateTargetInput, type Target, type Week } from 'chery-contracts';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { orpc } from '../../apis/orpc-query';
import { Button } from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import { DefinitionList } from '../../components/ui/DefinitionList';
import { FormField, FormSection } from '../../components/ui/FormField';
import { Input } from '../../components/ui/Input';
import { Notice } from '../../components/ui/Notice';
import { Select, type SelectOption } from '../../components/ui/Select';
import { Sheet } from '../../components/ui/Sheet';
import { cn } from '../../lib/cn';
import { errorData, isConflictError, nullableSelectOptions, serverMessage } from '../../lib/form-helpers';
import { PILLAR_CLASSES, PILLAR_META } from '../../lib/pillars';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import type { KpiRow } from '../kpis/kpi-display';
import { weekDateLabel } from '../tracker/tracker-display';
import {
  compatibleKpisByPillar,
  directionText,
  keepCompatible,
  overlapConflictFrom,
  periodText,
  targetValueText,
  yearOptions,
} from './target-display';

export type TargetSheetMode = 'create' | 'edit' | 'view';

export interface TargetSheetProps {
  isOpen: boolean;
  mode: TargetSheetMode;
  target?: Target;
  /** Every KPI the caller may see, inactive included — the link choices. */
  kpis: readonly KpiRow[];
  weeks: readonly Week[];
  onClose: () => void;
  onSaved: (name: string, created: boolean) => void;
}

type TargetForm = CreateTargetInput;

const EMPTY: TargetForm = {
  name: '',
  value: Number.NaN,
  unit: 'NUMBER',
  currency: null,
  direction: 'AT_LEAST',
  periodType: 'ANNUAL',
  year: new Date().getFullYear(),
  startWeekId: null,
  endWeekId: null,
  status: 'ACTIVE',
  kpiIds: [],
};

const toForm = (target: Target): TargetForm => ({
  name: target.name,
  value: target.value,
  unit: target.unit,
  currency: target.currency,
  direction: target.direction,
  periodType: target.periodType,
  year: target.year,
  startWeekId: target.startWeekId,
  endWeekId: target.endWeekId,
  status: target.status,
  kpiIds: target.kpis.map((kpi) => kpi.id),
});

/**
 * Create, edit or view a target (plan §3.1, §7). The form validates against the contract's `createTargetInputSchema`;
 * the KPI chips offer only KPIs of the chosen unit (and currency), grouped by pillar, and a unit change drops the
 * chips that no longer fit. The service re-checks everything — an overlap with another active target comes back
 * as a 409 naming that target, shown here as is.
 */
export const TargetSheet = ({ isOpen, mode, target, kpis, weeks, onClose, onSaved }: TargetSheetProps) => {
  const { t } = useI18nTranslation();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [conflictName, setConflictName] = useState<string | null>(null);
  const createTarget = useMutation(orpc.targets.create.mutationOptions());
  const updateTarget = useMutation(orpc.targets.update.mutationOptions());

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<TargetForm>({ resolver: zodResolver(createTargetInputSchema), defaultValues: EMPTY });

  useEffect(() => {
    if (isOpen) {
      setSubmitError(null);
      setConflictName(null);
      reset(target && mode !== 'create' ? toForm(target) : EMPTY);
    }
  }, [isOpen, target, mode, reset]);

  const unit = watch('unit');
  const currency = watch('currency') ?? null;
  const periodType = watch('periodType');
  const effectiveCurrency = unit === 'CURRENCY' ? currency : null;
  const groups = useMemo(() => compatibleKpisByPillar(kpis, unit, effectiveCurrency), [kpis, unit, effectiveCurrency]);

  // A unit or currency change drops the linked KPIs that no longer fit, rather than letting the service refuse them.
  useEffect(() => {
    const selected = getValues('kpiIds');
    const kept = keepCompatible(selected, kpis, unit, effectiveCurrency);

    if (kept.length !== selected.length) {
      setValue('kpiIds', kept);
    }
  }, [unit, effectiveCurrency, kpis, getValues, setValue]);

  const unitOptions: SelectOption[] = unitSchema.options
    .filter((value) => value !== 'TEXT')
    .map((value) => ({ value, label: t(`enums.unit.${value}`) }));
  const currencyOptions: SelectOption[] = currencySchema.options.map((value) => ({ value, label: value }));
  const years = yearOptions(weeks);
  const weekOptions: SelectOption[] = weeks.map((week) => ({ value: week.id, label: `${weekDateLabel(week.weekDate)} · ${week.label}` }));

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);
    setConflictName(null);

    const payload = {
      ...values,
      currency: values.unit === 'CURRENCY' ? (values.currency ?? null) : null,
      year: values.periodType === 'ANNUAL' ? (values.year ?? null) : null,
      startWeekId: values.periodType === 'CUSTOM' ? (values.startWeekId ?? null) : null,
      endWeekId: values.periodType === 'CUSTOM' ? (values.endWeekId ?? null) : null,
    };

    try {
      const saved =
        mode === 'edit' && target ? await updateTarget.mutateAsync({ id: target.id, ...payload }) : await createTarget.mutateAsync(payload);

      onSaved(saved.name, mode !== 'edit');
    } catch (error) {
      setConflictName(isConflictError(error) ? (overlapConflictFrom(errorData(error))?.targetName ?? null) : null);
      setSubmitError(serverMessage(error) ?? t('errors.saveFailed'));
    }
  });

  if (mode === 'view' && target) {
    return (
      <Sheet
        isOpen={isOpen}
        onClose={onClose}
        title={t('targets.form.viewTitle')}
        description={target.name}
      >
        <DefinitionList
          items={[
            { label: t('targets.form.name'), value: target.name },
            { label: t('targets.form.value'), value: `${directionText(target.direction)} ${targetValueText(target)}` },
            { label: t('targets.form.period'), value: periodText(target) },
            { label: t('targets.columns.kpis'), value: target.kpis.map((kpi) => `${kpi.name} (${kpi.code})`).join(', ') || '—' },
            { label: t('targets.columns.status'), value: t(`enums.status.${target.status}`) },
          ]}
        />
      </Sheet>
    );
  }

  const isEdit = mode === 'edit';

  return (
    <Sheet
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? t('targets.form.editTitle') : t('targets.form.createTitle')}
      description={t('targets.form.subtitle')}
      className="max-w-lg"
      footer={
        <>
          <Button
            variant="outline"
            onClick={onClose}
          >
            {t('actions.cancel')}
          </Button>
          <Button
            type="submit"
            form="target-form"
            isLoading={isSubmitting}
          >
            {isSubmitting ? t('actions.saving') : t('actions.save')}
          </Button>
        </>
      }
    >
      <form
        id="target-form"
        noValidate
        onSubmit={onSubmit}
        className="space-y-6"
      >
        <FormSection title={t('targets.form.sectionTarget')}>
          <FormField
            id="target-name"
            label={t('targets.form.name')}
            error={errors.name?.message}
          >
            <Input
              id="target-name"
              hasError={Boolean(errors.name)}
              {...register('name')}
            />
          </FormField>

          <div className="grid grid-cols-2 gap-3">
            <FormField
              id="target-direction"
              label={t('targets.form.direction')}
            >
              <Select
                id="target-direction"
                options={[
                  { value: 'AT_LEAST', label: t('targets.direction.AT_LEAST') },
                  { value: 'AT_MOST', label: t('targets.direction.AT_MOST') },
                ]}
                {...register('direction')}
              />
            </FormField>
            <FormField
              id="target-value"
              label={t('targets.form.value')}
              error={errors.value?.message}
            >
              <Input
                id="target-value"
                type="number"
                step="any"
                min="0"
                hasError={Boolean(errors.value)}
                {...register('value', { valueAsNumber: true })}
              />
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <FormField
              id="target-unit"
              label={t('targets.form.unit')}
            >
              <Select
                id="target-unit"
                options={unitOptions}
                {...register('unit')}
              />
            </FormField>
            {unit === 'CURRENCY' ? (
              <FormField
                id="target-currency"
                label={t('targets.form.currency')}
                error={errors.currency?.message}
              >
                <Select
                  id="target-currency"
                  placeholder={t('kpis.form.currencyPlaceholder')}
                  options={currencyOptions}
                  {...register('currency', nullableSelectOptions)}
                />
              </FormField>
            ) : null}
          </div>
        </FormSection>

        <FormSection title={t('targets.form.period')}>
          <div className="flex items-center gap-5 text-sm">
            {(['ANNUAL', 'CUSTOM'] as const).map((value) => (
              <label
                key={value}
                className="inline-flex items-center gap-2"
              >
                <input
                  type="radio"
                  value={value}
                  data-target-period={value}
                  className="accent-primary"
                  {...register('periodType')}
                />
                {t(`targets.period.${value}`)}
              </label>
            ))}
          </div>

          {periodType === 'ANNUAL' ? (
            <FormField
              id="target-year"
              label={t('targets.form.year')}
              error={errors.year?.message}
            >
              <Select
                id="target-year"
                options={years.map((year) => ({ value: String(year), label: String(year) }))}
                {...register('year', {
                  setValueAs: (value: unknown) => (value === '' || value === null || value === undefined ? null : Number(value)),
                })}
              />
            </FormField>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <FormField
                id="target-start"
                label={t('targets.form.startWeek')}
              >
                <Select
                  id="target-start"
                  placeholder={t('targets.form.chooseWeek')}
                  options={weekOptions}
                  {...register('startWeekId', nullableSelectOptions)}
                />
              </FormField>
              <FormField
                id="target-end"
                label={t('targets.form.endWeek')}
              >
                <Select
                  id="target-end"
                  placeholder={t('targets.form.chooseWeek')}
                  options={weekOptions}
                  {...register('endWeekId', nullableSelectOptions)}
                />
              </FormField>
            </div>
          )}

          <Controller
            control={control}
            name="status"
            render={({ field }) => (
              <Checkbox
                id="target-active"
                label={t('targets.form.active')}
                checked={field.value !== 'INACTIVE'}
                onChange={(event) => field.onChange(event.target.checked ? 'ACTIVE' : 'INACTIVE')}
              />
            )}
          />
        </FormSection>

        <FormSection title={t('targets.form.kpis')}>
          <p className="text-xs text-muted-foreground">{t('targets.form.kpisHint')}</p>
          <Controller
            control={control}
            name="kpiIds"
            render={({ field }) => (
              <div
                className="space-y-3"
                data-target-kpis
              >
                {groups.length === 0 ? <p className="text-sm text-muted-foreground">{t('targets.form.noCompatible')}</p> : null}
                {groups.map((group) => (
                  <div key={group.pillar}>
                    <p className={cn('mb-1.5 text-xs font-semibold', PILLAR_CLASSES[group.pillar].text)}>{PILLAR_META[group.pillar].label}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {group.kpis.map((kpi) => {
                        const selected = field.value.includes(kpi.id);

                        return (
                          <button
                            key={kpi.id}
                            type="button"
                            aria-pressed={selected}
                            data-kpi-chip={kpi.code}
                            onClick={() => field.onChange(selected ? field.value.filter((id) => id !== kpi.id) : [...field.value, kpi.id])}
                            className={cn(
                              'rounded-full border px-2.5 py-1 text-xs transition-colors',
                              selected
                                ? 'border-primary bg-primary text-primary-foreground'
                                : 'border-border bg-background text-foreground hover:bg-muted',
                              kpi.status === 'INACTIVE' && !selected && 'text-muted-foreground',
                            )}
                          >
                            {kpi.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {errors.kpiIds?.message ? <p className="text-xs text-destructive">{errors.kpiIds.message}</p> : null}
              </div>
            )}
          />
        </FormSection>

        {submitError ? (
          <Notice
            tone="danger"
            title={conflictName ? t('targets.overlapTitle', { name: conflictName }) : undefined}
          >
            <span data-target-error>{submitError}</span>
          </Notice>
        ) : null}
      </form>
    </Sheet>
  );
};
